/**
 * EvalCaseHoverCard — hover-triggered detail popup for one Ion eval case.
 *
 * The run configurator's case picker can only show `caseName` + `[suite]` per
 * row, but the field that actually decides whether you want a case in a run is
 * its `prompt`, which was invisible everywhere in that drawer. This renders the
 * whole case (prompt, expectations, which graders it needs, metadata, notes) on
 * hover without costing a click or a layout row.
 *
 * Positioning is the same recipe as `analytics/shared/InfoTooltip.jsx`
 * (portal into document.body + getBoundingClientRect + flip-above on viewport
 * overflow) — deliberately COPIED rather than imported: InfoTooltip is
 * click-triggered, `?`-button-anchored and used all over the analytics tab, so
 * folding a hover mode into it would put every one of those call sites at risk.
 * Only the ~20 lines of geometry are shared in spirit, not the component.
 */
import { createPortal } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { describeCaseExpectations } from './ionEvalHelp.js';

const CARD_WIDTH = 380;
// Only used to decide flip-above vs. below. The real card is auto-height; an
// over-estimate here just means we flip a little eagerly, which is harmless.
const CARD_EST_HEIGHT = 340;
const OPEN_DELAY_MS = 200;

/**
 * Viewport-aware fixed position for a card anchored to `rect`.
 * Prefers below-and-left-aligned, flips above when the bottom would overflow.
 */
export function computeHoverCardPosition(rect, viewport) {
  const vw = viewport?.width ?? 0;
  const vh = viewport?.height ?? 0;

  let left = rect.left;
  left = Math.max(8, Math.min(left, vw - CARD_WIDTH - 8));

  let top = rect.bottom + 8;
  if (top + CARD_EST_HEIGHT > vh) {
    top = rect.top - 8 - CARD_EST_HEIGHT;
    if (top < 8) top = 8;
  }
  return { top, left };
}

function Chips({ items, tone }) {
  if (!items || !items.length) return null;
  return (
    <span className="ion-eval-hovercard-chips">
      {items.map((t) => (
        <span key={t} className={`ion-eval-hovercard-chip ion-eval-hovercard-chip--${tone}`}>
          {t}
        </span>
      ))}
    </span>
  );
}

export function EvalCaseHoverCard({ case: evalCase, viaSuite = null, children }) {
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const timerRef = useRef(null);

  const close = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPos(null);
  }, []);

  const handleEnter = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPos(computeHoverCardPosition(rect, { width: window.innerWidth, height: window.innerHeight }));
    }, OPEN_DELAY_MS);
  }, []);

  // A scroll inside the picker box (or a resize) invalidates the snapshot the
  // fixed position was computed from — drop the card instead of letting it
  // float somewhere unrelated.
  useEffect(() => {
    if (!pos) return undefined;
    const onGone = () => close();
    window.addEventListener('scroll', onGone, true);
    window.addEventListener('resize', onGone);
    return () => {
      window.removeEventListener('scroll', onGone, true);
      window.removeEventListener('resize', onGone);
    };
  }, [pos, close]);

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  if (!evalCase) return children;

  const d = describeCaseExpectations(evalCase);

  return (
    <span
      ref={anchorRef}
      className="ion-eval-hovercard-anchor"
      onMouseEnter={handleEnter}
      onMouseLeave={close}
    >
      {children}
      {pos &&
        createPortal(
          <div
            className="chart-info-popup ion-eval-hovercard"
            style={{
              position: 'fixed',
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              width: `${CARD_WIDTH}px`,
              maxWidth: `${CARD_WIDTH}px`,
              zIndex: 99999,
            }}
          >
            {/* 1 — identity + status badges */}
            <div className="ion-eval-hovercard-title">
              <span className="ion-eval-hovercard-name">{evalCase.caseName}</span>
              {!evalCase.isActive && (
                <span className="ion-eval-hovercard-badge ion-eval-hovercard-badge--warn">
                  inactive
                </span>
              )}
              {viaSuite && (
                <span className="ion-eval-hovercard-badge">via suite "{viaSuite}"</span>
              )}
            </div>

            {/* 2 — the prompt: the single most decision-relevant field */}
            <div className="ion-eval-hovercard-section">
              <div className="ion-eval-hovercard-label">Prompt</div>
              <div className="ion-eval-hovercard-prompt">{evalCase.prompt || '—'}</div>
            </div>

            {/* 3 — what the deterministic scorer will assert */}
            <div className="ion-eval-hovercard-section">
              <div className="ion-eval-hovercard-label">Expectations</div>
              {d.hasExpectations ? (
                <div className="ion-eval-hovercard-expect">
                  {d.expectTools.length > 0 && (
                    <div>
                      expects <Chips items={d.expectTools} tone="ok" />
                    </div>
                  )}
                  {d.forbidTools.length > 0 && (
                    <div>
                      forbids <Chips items={d.forbidTools} tone="bad" />
                    </div>
                  )}
                  {(d.outputContainsCount > 0 || d.outputExcludesCount > 0) && (
                    <div>
                      {d.outputContainsCount > 0 && `${d.outputContainsCount} must-contain`}
                      {d.outputContainsCount > 0 && d.outputExcludesCount > 0 && ' · '}
                      {d.outputExcludesCount > 0 && `${d.outputExcludesCount} must-exclude`}
                      {' substring(s)'}
                    </div>
                  )}
                  {d.triStates.length > 0 && (
                    <div>
                      <Chips items={d.triStates} tone="neutral" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="ion-eval-hovercard-muted">
                  No deterministic assertion — only liveness/graders apply.
                </div>
              )}
            </div>

            {/* 4 — will the run I'm configuring actually grade this case? */}
            <div className="ion-eval-hovercard-section">
              <div className="ion-eval-hovercard-label">Graders needed</div>
              {d.graders.length ? (
                <Chips items={d.graders.map((g) => g.label)} tone="neutral" />
              ) : (
                <div className="ion-eval-hovercard-muted">
                  None — no rubric text and no reference answer, so an LLM grader has nothing
                  to score here.
                </div>
              )}
            </div>

            {/* 5 — metadata */}
            <div className="ion-eval-hovercard-section ion-eval-hovercard-meta">
              {[
                evalCase.suite || 'no suite',
                (evalCase.tags || []).join(', ') || 'no tags',
                evalCase.confidence || 'no confidence',
                evalCase.lang || 'no lang',
                evalCase.provenance || 'no provenance',
              ].join(' · ')}
            </div>

            {/* 6 — why the case exists */}
            {evalCase.notes && (
              <div className="ion-eval-hovercard-section">
                <div className="ion-eval-hovercard-label">Notes</div>
                <div className="ion-eval-hovercard-notes">{evalCase.notes}</div>
              </div>
            )}
          </div>,
          document.body
        )}
    </span>
  );
}
